<template>
  <div class="page-header align-items-start min-vh-screen mbr-account">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <header class="mbr-account__bar">
        <h1 class="mbr-account__title">{{ $t('account.title') }}</h1>
      </header>

      <div class="mbr-account__body">
        <button type="button" class="mbr-account__profile" @click="go('/profile')">
          <span class="mbr-account__who">
            <span class="mbr-account__avatar" aria-hidden="true">
              <template v-if="initials">{{ initials }}</template>
              <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </span>
            <span class="mbr-account__info">
              <span class="mbr-account__name">{{ fullName || phone }}</span>
              <span v-if="fullName && phone" class="mbr-account__phone">{{ phone }}</span>
              <span v-if="member.tier_name" class="mbr-account__tier">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                <span>{{ member.tier_name }}</span>
              </span>
            </span>
          </span>
          <span class="mbr-account__edit">
            <span>{{ $t('account.edit') }}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
          </span>
        </button>

        <section v-for="group in visibleGroups" :key="group.label" class="mbr-account__group">
          <div class="mbr-account__label">{{ group.label }}</div>
          <div class="mbr-account__card">
            <button
              v-for="row in group.rows"
              :key="row.text"
              type="button"
              class="mbr-account__row"
              @click="go(row.path)"
            >
              <span class="mbr-account__row-main">
                <span class="mbr-account__icon" aria-hidden="true" v-html="row.icon"></span>
                <span class="mbr-account__row-text">{{ row.text }}</span>
              </span>
              <svg class="mbr-account__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </section>

        <!-- same as the sidenav's Logout; no chevron in the design -->
        <div class="mbr-account__card">
          <button type="button" class="mbr-account__row mbr-account__row--logout" @click="logOut()">
            <span class="mbr-account__row-main">
              <span class="mbr-account__icon mbr-account__icon--logout" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </span>
              <span class="mbr-account__row-text">{{ $t('account.log_out') }}</span>
            </span>
          </button>
        </div>
      </div>
    </div>
    <QrcodeMember />
    <MbrBottomNav />
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import QrcodeMember from "@/components/QrcodeMember.vue";
import MbrBottomNav from "@/components/MbrBottomNav.vue";
import MemberService from "@/services/member.service";
import TokenService from "@/services/token.service";
import { localStorageName, getMerchantCode } from "@/config/utils";

// Icons copied from member_app_UI/song_fa_members_account_hub_mobile/code.html
const svg = (paths) =>
  `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">${paths}</svg>`;
const ICONS = {
  topup: svg('<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>'),
  donation: svg('<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>'),
  store: svg('<path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/>'),
  gift: svg('<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>'),
  trophy: svg('<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>'),
  calendar: svg('<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>'),
  // not in the design (Attendance row was added on request): calendar with a check
  attendance: svg('<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/>'),
  pin: svg('<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>'),
  friends: svg('<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>'),
  faq: svg('<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>'),
  phone: svg('<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>'),
};

export default {
  name: "Account",
  components: {
    QrcodeMember,
    MbrBottomNav,
  },
  data() {
    return {
      member: {},
      memberPermissions: [],
      checkMerchantConnectedStripe: "false",
    };
  },
  computed: {
    fullName() {
      return [this.member.name, this.member.last_name].filter(Boolean).join(" ");
    },
    initials() {
      return [this.member.name, this.member.last_name]
        .filter(Boolean)
        .map((part) => part.trim().charAt(0).toUpperCase())
        .join("");
    },
    phone() {
      if (!this.member.phone_number) return "";
      return (this.member.phone_code ? `+${this.member.phone_code} ` : "") + this.member.phone_number;
    },
    // Each row is shown under the same condition as its entry in common/Sidenav/SidenavList.vue
    visibleGroups() {
      const can = (permission) => this.memberPermissions.includes(permission);
      const stripe = this.checkMerchantConnectedStripe === "true";
      const groups = [
        {
          label: this.$t("account.group_wallet_rewards"),
          rows: [
            { text: this.$t("account.top_up"), path: "/top-up", icon: ICONS.topup, show: stripe && can("Top-up") },
            { text: this.$t("account.donation"), path: "/donation", icon: ICONS.donation, show: stripe && can("Donation") },
            { text: this.$t("account.store"), path: "/store", icon: ICONS.store, show: can("Reward Catelogue") },
            { text: this.$t("account.reward_catalogue"), path: "/reward-catalogue", icon: ICONS.gift, show: can("Reward Catelogue") },
            { text: this.$t("account.my_challenges"), path: "/my-campaigns", icon: ICONS.trophy, show: can("My Campaigns") },
          ],
        },
        {
          label: this.$t("account.group_services"),
          rows: [
            { text: this.$t("account.booking"), path: "/booking", icon: ICONS.calendar, show: stripe && !!this.member.phone_number },
            { text: this.$t("account.attendance"), path: "/admissions", icon: ICONS.attendance, show: can("Admissions") },
            { text: this.$t("account.find_branches"), path: "/find-branches", icon: ICONS.pin, show: can("Find Branches") },
            { text: this.$t("account.invite_friends"), path: "/invite-friends", icon: ICONS.friends, show: can("Invite Friends") },
          ],
        },
        {
          label: this.$t("account.group_support"),
          rows: [
            { text: this.$t("account.faq"), path: "/faq", icon: ICONS.faq, show: can("FAQ") },
            { text: this.$t("account.contact_us"), path: "/contact-us", icon: ICONS.phone, show: can("Contact Us") },
          ],
        },
      ];
      return groups
        .map((group) => ({ ...group, rows: group.rows.filter((row) => row.show) }))
        .filter((group) => group.rows.length);
    },
  },
  created() {
    this.checkMerchantConnectedStripe = localStorage.getItem(localStorageName("IS_CHECK_MERCHANT_CONNECTED_STRIPE")) ?? "false";
    MemberService.getMemberInfo().then(
      (response) => {
        if (response.data.result.isSuccess) {
          this.member = response.data.data;
        }
      },
      (error) => {
        console.log(error);
      }
    );
    if (TokenService.getLocalAccessToken()) {
      MemberService.checkMemberPermissions().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.memberPermissions = response.data.data;
          }
        },
        (error) => {
          console.log(error);
        }
      );
    }
  },
  // Same shell as Home: hide the top navbar (the bottom nav replaces it) and make the sidenav an
  // off-screen drawer at every width (#app.mbr-sidenav-overlay in custom.css), so the mobile
  // column and MbrBottomNav stay centred on desktop.
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },
  beforeUnmount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    // back to the legacy layout: App.vue pins the sidenav above 1200px
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },
  methods: {
    ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
    go(path) {
      const code = getMerchantCode();
      this.$router.push((code ? `/${code}` : "") + path);
    },
    // SidenavList: dispatch auth/logout, then its router link goes to sign-in
    logOut() {
      this.$store.dispatch("auth/logout");
      this.go("/sign-in");
    },
  },
};
</script>

<style scoped>
/* MBR073 Account hub — member_app_UI/song_fa_members_account_hub_mobile */

/* Material Dashboard's `.page-header { overflow: hidden }` would stop the header sticking */
.mbr-account.page-header {
  overflow: visible;
  overflow-x: clip;
  /* room for the fixed MbrBottomNav */
  padding-bottom: 120px;
  font-family: var(--mbr-font-body);
}

/* Store/RewardCatalogue load an unscoped `* { font-family: Poppins }` app-wide */
.mbr-account :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-account .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

/* Mobile column like the design (and Home / MyProfile) */
.mbr-account .container {
  max-width: 440px;
  margin: 0 auto;
}

.mbr-account__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 12px;
  background: var(--mbr-color-canvas);
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-account .mbr-account__title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 24px;
  font-weight: 800;
  line-height: 32px;
  letter-spacing: -0.025em;
  color: var(--mbr-color-ink-headline);
}

/* MBR075 profile card + grouped rows */
.mbr-account__body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 20px;
}

button.mbr-account__profile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 16px;
  text-align: left;
  background: var(--mbr-color-card);
  border: 1px solid var(--mbr-color-tint-dark);
  border-radius: 24px;
  box-shadow: 0 6px 20px rgba(var(--mbr-color-primary-rgb), 0.05);
  transition: background-color 0.15s ease;
}

button.mbr-account__profile:hover {
  background: #FFFDFD;
}

.mbr-account__who {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.mbr-account .mbr-account__avatar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  font-family: var(--mbr-font-headline);
  font-size: 22px;
  font-weight: 800;
  color: var(--mbr-color-primary);
  background: var(--mbr-color-peach);
  border: 2px solid var(--mbr-color-surface);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-shadow-rgb), 0.05);
}

.mbr-account__info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.mbr-account .mbr-account__name {
  font-family: var(--mbr-font-headline);
  font-size: 19px;
  font-weight: 800;
  line-height: 1.25;
  color: var(--mbr-color-ink-strong);
  overflow-wrap: anywhere;
}

.mbr-account__phone {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-account__tier {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 2px 10px;
  font-size: 10px;
  font-weight: 700;
  line-height: 15px;
  color: var(--mbr-color-peach);
  background: var(--mbr-color-primary);
  border-radius: var(--mbr-radius-full);
}

.mbr-account__tier svg {
  flex: 0 0 auto;
}

.mbr-account__edit {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  padding-right: 4px;
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-primary);
}

/* a div, not h2: custom.css forces mobile `h2 { font-size: 22px !important }` */
.mbr-account .mbr-account__label {
  margin: 0 0 8px;
  padding: 0 8px;
  font-family: var(--mbr-font-body);
  font-size: var(--mbr-label-caps-size);
  font-weight: var(--mbr-label-caps-weight);
  line-height: var(--mbr-label-caps-line);
  letter-spacing: var(--mbr-label-caps-tracking);
  text-transform: uppercase;
  color: var(--mbr-color-ink-soft);
}

.mbr-account__card {
  overflow: hidden;
  background: var(--mbr-color-card);
  border: 1px solid var(--mbr-color-tint-dark);
  border-radius: var(--mbr-radius-lg);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-shadow-rgb), 0.05);
}

/* every row is a <button> */
.mbr-account .mbr-account__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 56px;
  padding: 0 16px;
  text-align: left;
  text-decoration: none;
  background: none;
  border: 0;
  transition: background-color 0.15s ease;
}

.mbr-account .mbr-account__row + .mbr-account__row {
  border-top: 1px solid var(--mbr-color-tint);
}

.mbr-account .mbr-account__row:hover {
  background: var(--mbr-color-canvas);
}

.mbr-account__row-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mbr-account__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--mbr-color-primary);
  background: var(--mbr-color-tint);
  border-radius: var(--mbr-radius-full);
}

.mbr-account__row-text {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

.mbr-account__chevron {
  color: var(--mbr-color-ink-secondary);
}

/* MBR076 Log out: maroon text, pale pink icon circle */
.mbr-account .mbr-account__row--logout .mbr-account__row-text {
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--mbr-color-primary);
}

.mbr-account__icon--logout {
  background: rgba(var(--mbr-color-palepink-rgb), 0.4);
}
</style>
